import { useMarketLocationId, useTokenInfo } from '@e2m-customer-portal/hooks';
import { ErrorAlert, ErrorAlertProps } from '@e2m-customer-portal/ui';
import clsx from 'clsx';
import { TFunction } from 'i18next';
import { ReactElement, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useEffectOnce, useToggle } from 'react-use';
import { ErrorInfoIcon } from '../../assets/icons';
import { root } from '../../core/routes';
import { getErrorTranslationKey, getRequestId } from '../../utils';
import { getErrorCode, userIds } from './error-code';

function chooseMessage(
  t: TFunction,
  children: ErrorAlertProps['children'],
  error: ErrorAlertProps['error'],
): string | undefined {
  if (typeof children === 'string') {
    return children;
  }

  if (typeof error === 'string') {
    return error;
  }
  // The following code will make sure that we aren't showing a double message on the error component when a generic error occurs.
  // The code will become unnecessary after we get rid of the error snackbar.
  if (error) {
    return getErrorTranslationKey(error) === 'SHARED_ERROR_UNKNOWN' ||
      getErrorTranslationKey(error) === 'INTERNAL_SERVER_ERROR'
      ? ''
      : t(getErrorTranslationKey(error));
  }
}

export function ErrorComponent({
  children,
  error,
  errorPrefix,
  ...props
}: Readonly<ErrorAlertProps>): ReactElement<ErrorAlertProps> {
  const { t } = useTranslation();
  const message = chooseMessage(t, children, error);
  const requestId = getRequestId(error);
  const date = useMemo(() => new Date().toISOString(), [error]);
  const marketLocationId = useMarketLocationId();
  const { email, roles } = useTokenInfo();
  const isAccessDeniedError = ['A403', 'A404', 'A405'].includes(getErrorCode(error));

  const userId =
    roles && Object.keys(userIds).includes([...roles][0])
      ? userIds[[...roles][0] as unknown as userIds].valueOf()
      : undefined;

  const [revealed, reveal] = useToggle(false);

  useEffectOnce(() => {
    setTimeout(() => reveal(true), 500);
  });

  return (
    <ErrorAlert error={error} errorPrefix={errorPrefix} {...props}>
      <div
        className={clsx(
          'm-4 flex-col items-center gap-y-4 rounded-lg border border-solid border-error-50 p-4 transition-opacity',
          revealed ? 'opacity-100' : 'opacity-0',
        )}
      >
        <div className="mt-0 flex items-start gap-x-2 text-error-500">
          <ErrorInfoIcon className="m-0 mt-2.5 w-6 min-w-6" />
          <div className="max-w-[60ch]">
            <Trans
              components={{
                h1: <h1 className="mb-1.5 text-base">{}</h1>,
                p: <p className="mt-0.5" />,
                a: <Link className="font-semibold text-error-500" to={root.getLink()} target="_parent" />,
              }}
            >
              {isAccessDeniedError
                ? t('SHARED_ERROR_ACCESS_DENIED_ERROR_COMPONENT')
                : t('SHARED_ERROR_UNKNOWN_ERROR_COMPONENT')}
            </Trans>
          </div>
        </div>
        {message && !isAccessDeniedError && (
          <pre className="m-0 flex whitespace-pre-wrap rounded-md bg-error-10 p-4">
            <p className="mt-0 flex items-start gap-x-2 text-error-500">{message}</p>
          </pre>
        )}

        <ul className="mx-8 list-none p-0 text-sm text-grey-400">
          <li>{t('SHARED_ERROR_CODE', { code: getErrorCode(error) })}</li>
          {requestId && <li>{t('SHARED_ERROR_REQUEST', { id: requestId })}</li>}
          {userId && <li>{t('SHARED_ERROR_USER_TYPE', { id: userId })}</li>}
          {email && <li>{t('SHARED_ACCOUNT', { email })}</li>}
          {marketLocationId && <li>{t('SHARED_ERROR_MARKET_LOCATION', { id: marketLocationId })}</li>}
          <li>{t('SHARED_ERROR_DATE', { date })}</li>
        </ul>
        {process.env.NODE_ENV !== 'production' && error && (
          <pre className="m-0 flex whitespace-pre-wrap break-all rounded-md bg-error-10 p-4">
            {typeof error === 'object' ? error.message : error}
          </pre>
        )}
      </div>
    </ErrorAlert>
  );
}
